import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import purchaseOrderService, { PurchaseOrderAccess } from "../services/purchaseOrderService";

interface PurchaseOrderAccessContextValue {
  access: PurchaseOrderAccess | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const PurchaseOrderAccessContext = createContext<PurchaseOrderAccessContextValue | undefined>(undefined);

export const PurchaseOrderAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [access, setAccess] = useState<PurchaseOrderAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAccess = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await purchaseOrderService.getAccess();
      setAccess(result);
    } catch (err) {
      setAccess(null);
      setError(err instanceof Error ? err.message : "Failed to load access.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAccess();
  }, [loadAccess]);

  return (
    <PurchaseOrderAccessContext.Provider value={{ access, loading, error, refresh: loadAccess }}>
      {children}
    </PurchaseOrderAccessContext.Provider>
  );
};

export const usePurchaseOrderAccess = (): PurchaseOrderAccessContextValue => {
  const context = useContext(PurchaseOrderAccessContext);
  if (!context) {
    throw new Error("usePurchaseOrderAccess must be used within a PurchaseOrderAccessProvider");
  }
  return context;
};
